import { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import Login from "./Login";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const EMPTY_FORM = {
  title: "",
  description: "",
  priority: "MEDIUM",
  status: "TODO",
  dueDate: "",
};

const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };

function App() {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filter, setFilter] = useState("ALL");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/me`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.authenticated) {
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    }
  }, []);

  const loginWithEmail = async (data) => {
    setUser(data);
  };

  const registerWithEmail = async (data) => {
    setUser(data);
  };

  const demoLogin = async () => {
    const response = await fetch(`${API_BASE_URL}/api/demo-login`, {
      credentials: "include",
    });
    const data = await response.json();
    setUser(data);
  };

  const logout = async () => {
    await fetch(`${API_BASE_URL}/api/logout`, {
      method: "POST",
      credentials: "include",
    });
    setUser(null);
  };

  const fetchTasks = useCallback(async () => {
    if (!user) return;

    const response = await fetch(`${API_BASE_URL}/tasks`, {
      credentials: "include",
      headers: {
        "X-User-Email": user.email,
      },
    });
    if (response.status === 401 || response.status === 403) {
      setUser(null);
      return;
    }
    const data = await response.json();
    setTasks(data);
  }, [user]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user, fetchTasks]);

  const visibleTasks = useMemo(() => {
    const sorted = [...tasks].sort(
      (a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0)
    );

    if (filter === "ALL") return sorted;
    return sorted.filter((task) => task.status === filter);
  }, [tasks, filter]);

  const summary = useMemo(() => {
    return {
      total: tasks.length,
      todo: tasks.filter((task) => task.status === "TODO").length,
      doing: tasks.filter((task) => task.status === "DOING").length,
      done: tasks.filter((task) => task.status === "DONE").length,
    };
  }, [tasks]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addTask = async () => {
    if (!form.title.trim() || !user) return;

    setIsSubmitting(true);
    const response = await fetch(`${API_BASE_URL}/tasks`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-User-Email": user.email,
      },
      body: JSON.stringify({
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
      }),
    });

    if (response.ok) {
      setForm(EMPTY_FORM);
      await fetchTasks();
    }

    setIsSubmitting(false);
  };

  const deleteTask = async (id) => {
    await fetch(`${API_BASE_URL}/tasks/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "X-User-Email": user.email,
      },
    });
    fetchTasks();
  };

  const updateStatus = async (task, nextStatus) => {
    await fetch(`${API_BASE_URL}/tasks/${task.id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-User-Email": user.email,
      },
      body: JSON.stringify({ ...task, status: nextStatus }),
    });
    fetchTasks();
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date(new Date().setHours(0, 0, 0, 0));
  };

  if (!user) {
    return <Login onLogin={loginWithEmail} onRegister={registerWithEmail} onDemoLogin={demoLogin} />;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="user-row">
          <div>
            <p className="eyebrow">FocusFlow</p>
            <h1>今日の一手を決める</h1>
          </div>
          <div className="user-panel">
            {user.pictureUrl && <img src={user.pictureUrl} alt={user.name} className="user-avatar" />}
            <div>
              <strong>{user.name}</strong>
              <small>{user.email}</small>
            </div>
            <button type="button" className="logout-btn" onClick={logout} style={{ all: "unset", cursor: "pointer" }}>ログアウト</button>
          </div>
        </div>
      </header>

      <section className="summary-grid">
        <article className="summary-card">
          <span>総数</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="summary-card todo">
          <span>未着手</span>
          <strong>{summary.todo}</strong>
        </article>
        <article className="summary-card doing">
          <span>進行中</span>
          <strong>{summary.doing}</strong>
        </article>
        <article className="summary-card done">
          <span>完了</span>
          <strong>{summary.done}</strong>
        </article>
      </section>

      <section className="task-form card">
        <div className="field-row">
          <label>
            タスク名
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="例：設計レビューを終える"
            />
          </label>
        </div>

        <div className="field-row two-columns">
          <label>
            優先度
            <select name="priority" value={form.priority} onChange={handleChange}>
              <option value="HIGH">高</option>
              <option value="MEDIUM">中</option>
              <option value="LOW">低</option>
            </select>
          </label>

          <label>
            状態
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="TODO">未着手</option>
              <option value="DOING">進行中</option>
              <option value="DONE">完了</option>
            </select>
          </label>
        </div>

        <div className="field-row">
          <label>
            メモ
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="やることの背景や補足"
            />
          </label>
        </div>

        <div className="field-row single-field">
          <label>
            期限
            <input
              type="date"
              name="dueDate"
              value={form.dueDate}
              onChange={handleChange}
            />
          </label>
        </div>

        <button className="primary-btn" onClick={addTask} disabled={isSubmitting}>
          {isSubmitting ? "追加中..." : "タスクを追加"}
        </button>
      </section>

      <section className="task-board card">
        <div className="board-header">
          <h2>タスク一覧</h2>
          <div className="filter-group">
            {['ALL', 'TODO', 'DOING', 'DONE'].map((state) => (
              <button
                key={state}
                className={filter === state ? 'filter-btn active' : 'filter-btn'}
                onClick={() => setFilter(state)}
              >
                {state === 'ALL' ? '全件' : state === 'TODO' ? '未着手' : state === 'DOING' ? '進行中' : '完了'}
              </button>
            ))}
          </div>
        </div>

        <div className="task-list">
          {visibleTasks.length === 0 ? (
            <div className="empty-state">登録がまだありません。最初の一手を追加しましょう。</div>
          ) : (
            visibleTasks.map((task) => (
              <article key={task.id} className={`task-card priority-${task.priority.toLowerCase()}`}>
                <div className="task-header">
                  <div>
                    <p className="task-priority">{task.priority}</p>
                    <h3>{task.title}</h3>
                  </div>
                  <button className="delete-btn" onClick={() => deleteTask(task.id)}>削除</button>
                </div>

                {task.description && <p className="task-description">{task.description}</p>}

                <div className="task-meta">
                  <span className={isExpired(task.dueDate) ? 'deadline overdue' : 'deadline'}>
                    {task.dueDate || '期限なし'}
                  </span>
                  <span className="status-tag">{task.status}</span>
                </div>

                <div className="task-actions">
                  <button onClick={() => updateStatus(task, 'TODO')}>未着手</button>
                  <button onClick={() => updateStatus(task, 'DOING')}>進行中</button>
                  <button onClick={() => updateStatus(task, 'DONE')}>完了</button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

export default App;